import { clsx, type ClassValue } from "clsx";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { TInventory } from "~/types";

export * from "./utils";
export * from "./client-booking-filter";
export * from "./formatMentions";
export * from "./date-timezone.utils";

export function capitalize(str: string) {
  if (!str) return "";

  const isAllUpperCase = str === str.toUpperCase();

  if (isAllUpperCase) {
    // Handle all uppercase words with underscores (like AVAILABLE_ON_HAND)
    return str
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  }

  const spacedString = str
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (firstChar) => firstChar.toUpperCase()); // Ensure first letter is uppercase

  return spacedString
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .trim();
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString: string | Date | dayjs.Dayjs): string {
  const date = dayjs(dateString);

  if (!date.isValid()) {
    return "Invalid date";
  }
  return date.format("YYYY-MM-DD");
}

export const formatDateTime = (
  dateString: string | Date | dayjs.Dayjs
): string => {
  const date = dayjs(dateString);
  if (!date.isValid()) {
    return "Invalid date";
  }
  return date.format("YYYY-MM-DD HH:mm:ss");
};

export function formatToAMPM(datetime: Date | string | dayjs.Dayjs): string {
  const date = dayjs(datetime);

  if (!date.isValid()) {
    return "Invalid time";
  }

  return date.format("h:mm A");
}

/**
 * Gets the correct display datetime for a service ticket.
 * Reads time from servicedatetime, but if servicedatetime is stored as a placeholder
 * (noon UTC for date-only or midnight), uses startDatetime for the time portion.
 * 
 * @param serviceDatetime - The service datetime (may be a placeholder)
 * @param startDatetime - The start datetime (contains actual time)
 * @returns A dayjs object with the correct date and time
 */
export function getServiceDisplayTime(
  serviceDatetime: Date | string | dayjs.Dayjs | null | undefined,
  startDatetime?: Date | string | dayjs.Dayjs | null | undefined
): dayjs.Dayjs | null {
  if (!serviceDatetime) return null;

  const serviceDate = dayjs(serviceDatetime);
  if (!serviceDate.isValid()) return null;

  // Check if servicedatetime has meaningful time
  // - If hour is 12 and minute is 0 in UTC, it's likely a date-only placeholder (noon UTC)
  // - If hour is 0 and minute is 0 in local time, it might be midnight (also a placeholder)
  const serviceDatetimeUTC = serviceDate.utc();
  const serviceDatetimeLocal = serviceDate.local();
  const isNoonUTCPlaceholder = serviceDatetimeUTC.hour() === 12 && serviceDatetimeUTC.minute() === 0;
  const isMidnightLocal = serviceDatetimeLocal.hour() === 0 && serviceDatetimeLocal.minute() === 0;

  // If servicedatetime appears to be a placeholder and we have startDatetime, use startDatetime for time
  if (startDatetime && (isNoonUTCPlaceholder || isMidnightLocal)) {
    const startTimeData = dayjs(startDatetime);
    if (startTimeData.isValid()) {
      // Combine date from serviceDatetime with time from startDatetime
      return serviceDate
        .hour(startTimeData.hour())
        .minute(startTimeData.minute())
        .second(startTimeData.second());
    }
  }

  // Use servicedatetime directly (it should have the correct time)
  return serviceDate;
}

export function calculateCubicFeet(
  length: number | null,
  width: number | null,
  height: number | null
) {
  if (
    length !== null &&
    width !== null &&
    height !== null &&
    length > 0 &&
    width > 0 &&
    height > 0
  ) {
    // Convert from cubic inches to cubic feet (1728 cubic inches = 1 cubic foot)
    const cubicFeet = (length * width * height) / 1728;
    return cubicFeet.toFixed(2);
  }
  return "N/A";
}

export function calculateCubicFeetInventory(inventories: TInventory[]): {
  items: { title: string; inventoryRef: string; cubicFeet: string | number }[];
  totalWeight: number;
  totalCubicFeet: string | number;
} {
  let totalCubicFeet = 0;

  const items = inventories.map((inventory) => {
    const { length, width, height, title, inventoryRef } = inventory;

    if (
      length !== null &&
      width !== null &&
      height !== null &&
      length > 0 &&
      width > 0 &&
      height > 0
    ) {
      const cubicFeet = (length * width * height) / 1728;
      totalCubicFeet += parseFloat(cubicFeet.toFixed(2));
      return {
        title: title ?? "",
        inventoryRef: inventoryRef ?? "",
        cubicFeet: cubicFeet.toFixed(2),
      };
    }

    return {
      title: title ?? "",
      inventoryRef: inventoryRef ?? "",
      cubicFeet: "N/A",
    };
  });

  const finalTotalCubicFeet =
    totalCubicFeet > 0 ? totalCubicFeet.toFixed(2) : "N/A";

  // Calculate total weight, ensuring we parse strings as numbers
  const totalWeight = inventories.reduce((acc, inventory) => {
    const weight = inventory.weight ?? 0;
    const numericWeight = typeof weight === 'string' ? parseFloat(weight) : weight;
    return acc + (isNaN(numericWeight) ? 0 : numericWeight);
  }, 0);

  return {
    items,
    totalWeight: parseFloat(totalWeight.toFixed(2)),
    totalCubicFeet: finalTotalCubicFeet,
  };
}

export const getImageUrl = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${import.meta.env.VITE_API_URL}/${path}`;
};
